import type { Session } from '@supabase/supabase-js';
import type { LoaderFunctionArgs } from 'react-router-dom';
import { redirect } from 'react-router-dom';
import { getCurrentSession, buildLoginRedirect } from './auth';
import { supabase } from './supabase';

export type RootLoaderData = {
  session: Session | null;
};

export type ProtectedLoaderData = {
  session: Session;
};

export type LoginLoaderData = {
  redirectTo?: string;
};

export async function rootLoader(): Promise<RootLoaderData> {
  const session = await getCurrentSession();
  return { session };
}

export async function protectedLoader({
  request,
}: LoaderFunctionArgs): Promise<ProtectedLoaderData> {
  const session = await getCurrentSession();

  if (!session) {
    throw redirect(buildLoginRedirect(request));
  }

  // Check if user needs to complete onboarding (only if not already on onboarding page)
  const url = new URL(request.url);
  if (!url.pathname.includes('/onboarding')) {
    const { data: profile } = await supabase
      .from('profiles')
      .select('has_completed_onboarding')
      .eq('id', session.user.id)
      .single();

    if (profile && profile.has_completed_onboarding === false) {
      throw redirect('/onboarding');
    }
  }

  return { session };
}

export async function onboardingLoader({
  request,
}: LoaderFunctionArgs): Promise<ProtectedLoaderData> {
  const session = await getCurrentSession();

  if (!session) {
    throw redirect(buildLoginRedirect(request));
  }

  // Check if user has already completed onboarding
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('has_completed_onboarding')
    .eq('id', session.user.id)
    .single();

  // If onboarding is completed, redirect to app
  if (!profileError && profile && profile.has_completed_onboarding === true) {
    throw redirect('/app');
  }

  // If there's an error fetching profile, allow access (safer to show onboarding)
  // If onboarding is false or null, allow access to onboarding page

  return { session };
}

export async function loginLoader({
  request,
}: LoaderFunctionArgs): Promise<LoginLoaderData | null> {
  const session = await getCurrentSession();

  if (session) {
    // Check if user needs to complete onboarding
    const { data: profile } = await supabase
      .from('profiles')
      .select('has_completed_onboarding')
      .eq('id', session.user.id)
      .single();

    if (profile && profile.has_completed_onboarding === false) {
      throw redirect('/onboarding');
    }

    throw redirect('/app');
  }

  const url = new URL(request.url);
  const redirectTo = url.searchParams.get('redirectTo') || undefined;

  return redirectTo ? { redirectTo } : null;
}

/**
 * Loader for password reset page
 * This page needs to allow authenticated users (they're authenticated via reset tokens)
 * but should not redirect them away
 */
export async function resetPasswordLoader(): Promise<null> {
  // Allow both authenticated and unauthenticated users
  // The ResetPasswordPage component will handle token validation
  return null;
}
